import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { financeApi, formatCents } from "@/services/finance";
import { PageShell } from "@/shared/ui/PageHeader";
import { Card } from "@/shared/ui/Card";

const INP = "h-9 w-full rounded-md border border-slate-300 px-3 text-[13px] focus:border-blue-500 focus:outline-none";
const LBL = "text-[12px] font-medium text-slate-600";
const TERMS = ["NET 30", "NET 15", "预付"];
const termDays = (t: string) => (t === "NET 15" ? 15 : t === "预付" ? 0 : 30);
const addDays = (d: string, n: number) => new Date(new Date(d).getTime() + n * 86400_000).toISOString().slice(0, 10);

/** 记录新 Invoice（/admin/finance/invoices/new · canvas page-75 · 应收「开Invoice」prefill 进来）。 */
export function NewInvoicePage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const today = new Date().toISOString().slice(0, 10);

  // prefill from 应收「开 Invoice」(page-2 闭环)
  const preAmount = Number(sp.get("amount_cents")) || 0;
  const [orderNo] = useState(sp.get("order_no") || "");
  const [customer, setCustomer] = useState(sp.get("customer") || "");
  const [kind] = useState<"b2b" | "direct" | "api">((sp.get("customer_kind") as "b2b" | "direct" | "api") || "b2b");
  const [issued, setIssued] = useState(today);
  const [terms, setTerms] = useState("NET 30");
  const [amount, setAmount] = useState(preAmount ? String(preAmount / 100) : "");
  const currency = "USD"; // 系统统一 USD（USD-only 定调）· CNY 收款由财务手工对账
  const [memo, setMemo] = useState("");
  const [emailAfter, setEmailAfter] = useState(true);
  const due = addDays(issued, termDays(terms));

  const save = useMutation({
    mutationFn: (asDraft: boolean) =>
      financeApi.createInvoice({
        customer, customer_kind: kind, order_no: orderNo || null,
        amount_cents: Math.round(Number(amount) * 100), currency, memo: memo || null, payment_terms: terms,
        issued_at: new Date(issued).toISOString(), due_at: new Date(due).toISOString(),
      }).then(() => asDraft),
    onSuccess: (asDraft) => {
      if (!asDraft) alert("已保存。QB 推送压淡季 Phase 6 — 当前已本地存（未推 QB / 未发邮件）。");
      navigate("/admin/finance/receivables");
    },
    onError: (e) => alert((e as Error).message || "保存失败"),
  });
  const canSave = !!customer && Number(amount) > 0;

  return (
    <PageShell
      title="📄 记录新 Invoice"
      subtitle="关联订单 · 客户匹配 QB · 推 QB 同步 · 业务壳填字段比直接在 QB 录入更快"
      actions={
        <div className="flex gap-2">
          <button onClick={() => navigate(-1)} className="flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-[12px] font-medium text-slate-700 hover:bg-slate-50">取消</button>
          <button onClick={() => save.mutate(true)} disabled={!canSave || save.isPending} className="flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40">保存草稿</button>
        </div>
      }
    >
      {/* 1 · 关联订单 */}
      <Card title="🔗 关联订单（业务字段自动带入）" action={<span className="text-[12px] text-slate-500">选订单 → 客户 + 金额 + 币种 + 产品 自动填 · 比 QB 直接录入快</span>}>
        <label className="flex flex-col gap-1"><span className={LBL}>关联订单 <span className="text-rose-500">*</span></span>
          <input className={INP} value={orderNo} placeholder="如 #O-3148（从应收开票带入·或手填）" readOnly={!!orderNo} /></label>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="text-[12px] font-bold text-blue-800">🤖 自动带入字段</div>
            <div className="mt-1 space-y-0.5 text-[11px] text-slate-700">
              <div>· 客户：{customer || "—"}（{kind.toUpperCase()}）</div>
              <div>· 金额：{amount ? `${currency} ${formatCents(Number(amount) * 100)}` : "—"}</div>
              <div>· 订单号：{orderNo || "—"}</div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="text-[12px] font-bold text-blue-800">🔍 QB 客户匹配</div>
            <div className="mt-1 space-y-0.5 text-[11px] text-slate-500">
              <div>QB Customer：—（QB 未对接）</div>
              <div className="text-amber-700">⏳ QB 对接压淡季 Phase 6 · 现阶段本地建单</div>
            </div>
          </div>
        </div>
      </Card>

      {/* 2 · Invoice 字段 */}
      <Card title="📝 Invoice 字段">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="flex flex-col gap-1"><span className={LBL}>Invoice 号</span><input className={`${INP} bg-slate-50 text-slate-400`} value="系统自动生成" readOnly /></label>
          <label className="flex flex-col gap-1"><span className={LBL}>外部 Invoice 号（QB 生成）</span><input className={`${INP} bg-slate-50 text-slate-400`} value="QB 推送后回填（淡季）" readOnly /></label>
          <label className="flex flex-col gap-1"><span className={LBL}>开票日 <span className="text-rose-500">*</span></span><input type="date" className={INP} value={issued} onChange={(e) => setIssued(e.target.value)} /></label>
          <label className="flex flex-col gap-1"><span className={LBL}>到期日（按结算条件自动算）</span><input className={`${INP} bg-slate-50 text-slate-500`} value={`${due}（${terms}）`} readOnly /></label>
          <label className="flex flex-col gap-1"><span className={LBL}>客户 <span className="text-rose-500">*</span></span><input className={INP} value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="客户名（B2B 须已存在）" /></label>
          <label className="flex flex-col gap-1"><span className={LBL}>结算条件</span>
            <select className={INP} value={terms} onChange={(e) => setTerms(e.target.value)}>{TERMS.map((t) => <option key={t} value={t}>{t}</option>)}</select></label>
          <label className="flex flex-col gap-1"><span className={LBL}>金额 <span className="text-rose-500">*</span></span><input type="number" step="0.01" className={INP} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" /></label>
          <label className="flex flex-col gap-1"><span className={LBL}>币种 <span className="text-rose-500">*</span></span>
            <div className={`${INP} flex items-center bg-slate-50 font-mono text-slate-500`}>USD（系统统一）</div></label>
        </div>
        <label className="mt-3 flex flex-col gap-1"><span className={LBL}>备注 / Memo</span>
          <textarea rows={2} className={INP} value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="如 黄石 3 日游 · 6 人 · 含酒店+餐+门票" /></label>
      </Card>

      {/* 3 · QB 同步配置 */}
      <Card title="🔗 QB 同步配置">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <div className="text-[12px] font-bold text-emerald-800">✅ 推 QB 时会发生（QB 对接后）：</div>
          <ol className="mt-1.5 space-y-0.5 text-[11px] text-emerald-700">
            <li>1. QB 自动生成 PDF Invoice</li>
            <li>2. QB Sales Tax 自动算（按 Salt Lake City）</li>
            <li>3. QB Invoice 号回填到我们系统</li>
            <li>4. QB 自动给客户发邮件（如开启）</li>
            <li>5. 客户收款 → QB webhook 回推 → 我们自动标已收</li>
          </ol>
          <div className="mt-1.5 text-[10px] text-amber-700">⏳ QB 集成压淡季 Phase 6 · 现阶段「保存」仅本地建单</div>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
          <button onClick={() => setEmailAfter(true)} className={`flex flex-col items-start rounded-lg border px-4 py-2.5 text-left transition ${emailAfter ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
            <span className={`text-[12px] ${emailAfter ? "font-bold text-blue-800" : "font-semibold text-slate-900"}`}>{emailAfter ? "☑" : "☐"} 推 QB 后自动发邮件给客户</span>
            <span className="mt-0.5 text-[10px] text-slate-500">使用 QB 邮件模板</span>
          </button>
          <button onClick={() => setEmailAfter(false)} className={`flex flex-col items-start rounded-lg border px-4 py-2.5 text-left transition ${!emailAfter ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
            <span className={`text-[12px] ${!emailAfter ? "font-bold text-blue-800" : "font-semibold text-slate-900"}`}>{!emailAfter ? "☑" : "☐"} 只推 QB · 不发邮件</span>
            <span className="mt-0.5 text-[10px] text-slate-500">手动控制发送时机</span>
          </button>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => navigate(-1)} className="flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-[12px] font-medium text-slate-700 hover:bg-slate-50">取消</button>
          <button onClick={() => save.mutate(true)} disabled={!canSave || save.isPending} className="flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40">保存草稿（不推 QB）</button>
          <button onClick={() => save.mutate(false)} disabled={!canSave || save.isPending} className="flex h-9 items-center rounded-lg bg-blue-600 px-4 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">{save.isPending ? "保存中…" : "保存 + 推 QB + 发邮件"}</button>
        </div>
      </Card>
    </PageShell>
  );
}
